/** Human-readable labels for org-configured ingest / telematics sources. */

export const INGEST_SOURCE_LABELS: Record<string, string> = {
  optifleet: 'Optifleet',
  supatrak: 'Supatrak',
  samsara: 'Samsara',
  jigsaw: 'Jigsaw',
  eflex: 'Eflex',
  aquarius: 'Aquarius',
  qargo: 'Qargo',
  palletways: 'Palletways',
  same_day: 'Same Day TMS',
  declared: 'Same Day TMS',
};

const TELEMATICS_SOURCE_IDS = new Set(['optifleet', 'supatrak', 'samsara']);
const ORDER_SOURCE_IDS = new Set(['qargo', 'palletways', 'same_day', 'declared']);
/** Aquarius roster/compliance + Samsara EU tachograph hours (Darcica). */
const DRIVER_SOURCE_IDS = new Set(['aquarius', 'samsara']);
const FUEL_SOURCE_IDS = new Set(['jigsaw', 'eflex']);

export function formatIngestSourceLabel(id: string): string {
  const key = String(id || '').trim().toLowerCase();
  return INGEST_SOURCE_LABELS[key] ?? (key ? key.charAt(0).toUpperCase() + key.slice(1) : '');
}

/** "Samsara", "Optifleet + Supatrak", "Qargo, Palletways + …" */
export function formatIngestSourceList(ids: string[]): string {
  // Collapse declared → same_day so the badge shows one "Same Day TMS" label
  const normalized = ids.map((id) => {
    const key = String(id || '').trim().toLowerCase();
    return key === 'declared' ? 'same_day' : key;
  });
  const labels = [...new Set(normalized.map((id) => formatIngestSourceLabel(id)).filter(Boolean))];
  if (labels.length === 0) return '';
  if (labels.length === 1) return labels[0];
  if (labels.length === 2) return `${labels[0]} + ${labels[1]}`;
  return `${labels.slice(0, -1).join(', ')} + ${labels[labels.length - 1]}`;
}

export function pickTelematicsSourceIds(enabledOrConfigured: string[]): string[] {
  return enabledOrConfigured.filter((id) => TELEMATICS_SOURCE_IDS.has(String(id).toLowerCase()));
}

export function pickOrderSourceIds(enabled: string[]): string[] {
  return enabled.filter((id) => ORDER_SOURCE_IDS.has(String(id).toLowerCase()));
}

export function pickDriverSourceIds(enabled: string[]): string[] {
  return enabled.filter((id) => DRIVER_SOURCE_IDS.has(String(id).toLowerCase()));
}

export function pickFuelSourceIds(enabled: string[]): string[] {
  return enabled.filter((id) => FUEL_SOURCE_IDS.has(String(id).toLowerCase()));
}

/** e.g. "Loading live GPS data (Samsara)..." */
export function telematicsGpsLoadingMessage(providerIds: string[]): string {
  const list = formatIngestSourceList(providerIds);
  if (!list) return 'Loading live GPS data...';
  return `Loading live GPS data (${list})...`;
}

/** e.g. "Live vehicle location and movement analytics (Samsara)" */
export function telematicsRealtimeSubtitle(providerIds: string[]): string {
  const list = formatIngestSourceList(providerIds);
  if (!list) {
    return 'Live vehicle location and movement analytics';
  }
  return `Live vehicle location and movement analytics (${list})`;
}

/** e.g. "Compliance analytics… (Aquarius)" — prefer tab header badge for source; keep generic here */
export function driverComplianceSubtitle(providerIds: string[]): string {
  void providerIds;
  return 'Compliance analytics, risk assessment, defect tracking, and improvement insights';
}

/** e.g. "Advanced analytics… (Jigsaw)" — prefer tab header badge for source; keep generic here */
export function fuelAnalyticsSubtitle(providerIds: string[]): string {
  void providerIds;
  return 'Advanced analytics, savings opportunities, and predictive insights';
}

/** e.g. "Interactive analytics… (Palletways)" — prefer tab header badge for source; keep generic here */
export function ordersAnalyticsSubtitle(providerIds: string[]): string {
  void providerIds;
  return 'Interactive analytics with date range and status filtering';
}
